import React from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker as DatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';

const day = (value) => Date.parse(`${value}T12:00:00Z`);
const shiftedDate = (value, days) => new Date(day(value) + days * 86400000).toISOString().slice(0, 10);

export function updateCompleteWeekDate(form, key, value, latestDate, maxPeriodDays) {
  if (!value?.isValid() || !/^\d{4}-\d{2}-\d{2}$/.test(latestDate)) return null;
  const date = value.format('YYYY-MM-DD');
  const firstDate = dayjs(latestDate).subtract(3, 'year').format('YYYY-MM-DD');
  if (key === 'analysis_start') {
    if (value.day() !== 1 || date < firstDate || date > shiftedDate(latestDate, -6)) return null;
    const minimumEnd = shiftedDate(date, 6);
    const maximumEnd = [shiftedDate(date, maxPeriodDays - 1), latestDate].sort()[0];
    return { ...form, analysis_start: date,
      analysis_end: form.analysis_end >= minimumEnd && form.analysis_end <= maximumEnd ? form.analysis_end : minimumEnd };
  }
  if (key === 'analysis_end') {
    if (value.day() !== 0 || date < firstDate || date < shiftedDate(form.analysis_start, 6)
        || date > latestDate || date > shiftedDate(form.analysis_start, maxPeriodDays - 1)) return null;
    return { ...form, analysis_end: date };
  }
  return null;
}

export default function CompleteWeekPeriodFields({ form, latestDate, maxPeriodDays, comparison, onDateChange, onModeChange, disabled = false, action = null }) {
  const firstDate = dayjs(latestDate).subtract(3, 'year').format('YYYY-MM-DD');
  return <>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        {['analysis_start', 'analysis_end'].map((key) => {
          const label = key === 'analysis_start' ? 'Analysis start' : 'Analysis end';
          return <DatePicker key={key} label={label} value={form[key] ? dayjs(form[key]) : null}
            onChange={(value) => onDateChange(key, value)} inputFormat="DD.MM.YYYY"
            dayOfWeekFormatter={(name) => name.slice(0, 2)}
            shouldDisableDate={(value) => value.day() !== (key === 'analysis_start' ? 1 : 0)}
            minDate={dayjs(key === 'analysis_end' && form.analysis_start ? [firstDate, shiftedDate(form.analysis_start, 6)].sort()[1] : firstDate)}
            maxDate={key === 'analysis_start' ? dayjs(latestDate).subtract(6, 'day') : dayjs(form.analysis_start ? [latestDate, shiftedDate(form.analysis_start, maxPeriodDays - 1)].sort()[0] : latestDate)}
            OpenPickerButtonProps={{ 'aria-label': `Choose ${label.toLowerCase()}` }} disabled={disabled}
            renderInput={(params) => <TextField {...params} size="small" required sx={{ width: { xs: '100%', sm: 165 } }} inputProps={{ ...params.inputProps, readOnly: true }} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />} />;
        })}
      </LocalizationProvider>
      <TextField select label="Compare with" size="small" value={form.comparison_mode} onChange={(event) => onModeChange(event.target.value)} disabled={disabled} sx={{ width: { xs: '100%', sm: 235 } }}>
        <MenuItem value="previous_period">Previous period</MenuItem>
        <MenuItem value="last_year">Same weeks last year</MenuItem>
      </TextField>
      {action}
    </Box>
    <Typography variant="caption" sx={{ color: '#536a75', px: 0.25 }}>
      {comparison.comparison_start ? `Comparison period: ${comparison.comparison_start} – ${comparison.comparison_end}.` : 'The comparison period is selected automatically from your analysis dates.'}
      {form.comparison_mode === 'last_year' ? ' Shifted by 52 weeks to match weekdays.' : ''}
    </Typography>
  </>;
}
